import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import { ENVIRONMENT } from '@company-box/models';

export type AiDecalSide = 'INSIDE' | 'OUTSIDE';
export type AiDecalScope = 'WHOLE_BOX' | 'FACE';
export type AiDecalContentType = 'IMAGE' | 'SOLID_COLOR' | 'TEXT';

export interface AiDecalRequest {
  prompt: string;
  objectGuids?: string[];
  userId?: number;
  /**
   * The user's own earlier messages in this design conversation, oldest first, not including
   * `prompt`. Context only - lets the backend infer what an unscoped request refers to (e.g. "now
   * add stars too" after "snowflakes on the top") instead of asking; it never re-applies them.
   */
  history?: string[];
}

export interface AiDecalElementResult {
  objectGuid: string;
  /** Short code (e.g. "a1") the user can type in a follow-up prompt to target this element. */
  reference: string;
  scope: AiDecalScope;
  face: string | null;
  contentType: AiDecalContentType;
  designPrompt?: string;
  /** The prior guid this element replaced, or undefined if it's a brand new placement. */
  refinedObjectGuid?: string;
  createdAt?: string;
  /** Non-error caveat about this placement (e.g. a recolor request the backend couldn't apply). The element still placed normally. */
  note?: string;
  /** Ready-to-display sentence describing what happened to this element, e.g. "Rotated it to -90° on Top (outside)". */
  summary: string;
}

export interface AiDecalElementFailure {
  scope?: AiDecalScope;
  face?: string;
  contentType?: AiDecalContentType;
  message: string;
}

export interface AiClarifyingQuestion {
  question: string;
  /** 0 to ~4 concrete choices (e.g. panel names) when the ambiguity is a small enumerable set; empty for open-ended text. */
  options: string[];
}

export interface AiDecalResponse {
  containerName: string;
  side: AiDecalSide;
  elements: AiDecalElementResult[];
  failures: AiDecalElementFailure[];
  /** Set only when the request was ambiguous enough that nothing was placed - elements/failures are both empty in that case. */
  clarifyingQuestion?: AiClarifyingQuestion | null;
}

// Marks a message as written for the user (one of the fixed messages below) - as opposed to a
// caught JS error's own .message, which is an implementation detail and unsafe to show verbatim.
// Callers should only display error.message when the error is an AiUserFacingError.
export class AiUserFacingError extends Error {}

// Shown for network errors and 5xx responses other than 500 (e.g. a proxy-level 503/504, or a 502
// when Claude/Gemini failed behind our backend).
export const AI_COMMUNICATION_ERROR_MESSAGE = 'We could not reach the server. Please try again.';
export const AI_BUSY_ERROR_MESSAGE = 'The AI designer is busy right now. Please try again in a moment.';
export const AI_REQUEST_ERROR_MESSAGE = 'Something went wrong with that request. Please try again.';
// A 500 means our backend was reached and failed in its own code - "could not reach" would be untrue.
export const AI_SERVER_ERROR_MESSAGE = 'Something went wrong on our side. Please try again.';

// Picks the user-facing text for a failed AI request from its status alone. The response body is
// never shown: the backend writes it for developers (exception messages, raw upstream errors such
// as a Claude API 404), not for someone typing a design request.
export function aiErrorMessageFor(error: HttpErrorResponse): string {
  if (error.status === 429) {
    return AI_BUSY_ERROR_MESSAGE;
  }
  if (error.status >= 400 && error.status < 500) {
    return AI_REQUEST_ERROR_MESSAGE;
  }
  if (error.status === 500) {
    return AI_SERVER_ERROR_MESSAGE;
  }
  return AI_COMMUNICATION_ERROR_MESSAGE;
}

@Injectable({
  providedIn: "root",
})
export class DesignService {
  private httpClient: HttpClient = inject(HttpClient);
  constructor() {}
  private environment = inject(ENVIRONMENT);

  getPatternCollections(): Promise<any> {
    const url = `${this.environment.apiHost}/project/builder/pattern/collection/list?published=PUBLISHED`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error.message || error);
        }
      );
    });
  }

  getPatterns(collectionId: number, offset: number = 0, limit: number = 50): Promise<any> {
    const url = `${this.environment.apiHost}/project/builder/pattern/list?collection=${collectionId}&limit=${limit}&offset=${offset}&published=PUBLISHED`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error.message || error);
        }
      );
    });
  }

  getShapeCollections(): Promise<any> {
    const url = `${this.environment.apiHost}/project/builder/shape/collection/list?published=PUBLISHED`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error.message || error);
        }
      );
    });
  }

  getShapes(collectionId: number, offset: number = 0, limit: number = 50): Promise<any> {
    const url = `${this.environment.apiHost}/project/builder/shape/list?collection=${collectionId}&limit=${limit}&offset=${offset}&published=PUBLISHED`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error.message || error);
        }
      );
    });
  }

  getDesign(userID: number, containerID: string): Promise<any> {
    // Cache-bust: a local dev gateway/proxy in front of apiHost has been observed serving stale
    // cached responses (even 304s to non-GET requests) for these design endpoints, so every read
    // must be forced fresh rather than relying on the browser/proxy cache.
    const url = `${this.environment.apiHost}/project/design/${userID}/container/${containerID}?x=${Date.now()}`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  createDesign(userID: number, design: any): Promise<any> {
    const url = `${this.environment.apiHost}/project/design/${userID}/`;

    const payload = JSON.stringify(design);

    const headers = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };

    return new Promise<any>((resolve, reject) => {
      this.httpClient.post(url, payload, headers).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  updateDesign(userID: number, design: any): Promise<any> {
    const url = `${this.environment.apiHost}/project/design/${userID}/`;

    const payload = JSON.stringify(design);

    const headers = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };

    return new Promise<any>((resolve, reject) => {
      this.httpClient.put(url, payload, headers).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  deleteDesign(userId: number, designId: number): Promise<any> {
    const url = `${this.environment.apiHost}/project/design/${userId}/${designId}`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.delete(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  generateAiDecal(
    containerId: string,
    prompt: string,
    priorObjectGuids?: string[],
    userId?: number,
    history?: string[]
  ): Promise<AiDecalResponse> {
    const url = `${this.environment.apiHost}/project/container/${containerId}/design/decal`;
    const request: AiDecalRequest = {
      prompt,
      ...(priorObjectGuids?.length ? { objectGuids: priorObjectGuids } : {}),
      ...(userId ? { userId } : {}),
      ...(history?.length ? { history } : {}),
    };

    return new Promise<AiDecalResponse>((resolve, reject) => {
      this.httpClient.post<AiDecalResponse>(url, request).subscribe(
        (response) => {
          resolve(response);
        },
        (error) => {
          if (error instanceof HttpErrorResponse) {
            reject(new AiUserFacingError(aiErrorMessageFor(error)));
            return;
          }
          reject(error);
        }
      );
    });
  }

  deleteAiDecalElement(containerId: string, objectGuid: string): Promise<AiDecalElementResult> {
    const url = `${this.environment.apiHost}/project/container/${containerId}/design/decal/${objectGuid}`;

    return new Promise<AiDecalElementResult>((resolve, reject) => {
      this.httpClient.delete<AiDecalElementResult>(url).subscribe(
        (response) => {
          resolve(response);
        },
        (error) => {
          if (error instanceof HttpErrorResponse) {
            reject(new AiUserFacingError(aiErrorMessageFor(error)));
            return;
          }
          reject(error);
        }
      );
    });
  }
}
